/*
 * Financing partners — the ONE place lenders are defined (HFS Financing brief, 7 Oct 2026).
 *
 * Every financing button, lender card, comparison-table column and disclosure on the site renders from
 * `LENDERS`, so adding a third or fourth lender is one new entry here and nothing else. Components loop over
 * the array and lay out 2–4 lenders cleanly (side by side on desktop, stacked full-width on mobile).
 *
 * Rules from the brief:
 *   - link out only (target=_blank, rel="noopener sponsored"); never iframe or embed a lender page
 *   - buttons always appear together, never one without the other
 *   - the Acorn URL carries d=CE6G1 everywhere; on the calculator results it also gets &loanAmount=<amount>
 *     (the same whole-dollar amount the illustrative payment is based on). The HFS URL is never modified.
 *
 * The backend keeps a mirror of `id / name / url / buttonLabel / loanAmountParam` + the disclosures for the PDF and
 * emails (backend/financing.py LENDERS); tests/test_financing.py checks the two stay identical.
 */

export const LENDERS = [
  {
    id: 'acorn',
    name: 'Acorn Finance',
    url: 'https://www.acornfinance.com/pre-qualify/?d=CE6G1&utm_medium=user_pre_qual_link',
    buttonLabel: 'Pre-qualify with Acorn',
    amountRange: '$1,000–$100,000',
    pitch: 'Compare offers from 20+ lenders in about two minutes.',
    bullets: [
      'Compare offers from 20+ lenders side by side',
      'Fixed-rate personal loans, terms typically 2–12 years (up to 20 for well-qualified borrowers)',
      'Funds as soon as one business day after approval',
    ],
    facts: {
      loan_amounts: '$1,000–$100,000',
      loan_type: 'Fixed-rate personal loan',
      terms: 'Typically 2–12 years (up to 20 for well-qualified borrowers)',
      rate_check: 'Soft inquiry, no score impact',
      collateral: 'Most offers unsecured; some lenders offer secured loans',
      prepayment_penalty: 'None',
      origination_fees: 'None on loans over $40,000; some lenders charge 1–6% under $40,000 (no-fee options are flagged)',
      funding: 'As soon as 1 business day',
      funds_paid_to: 'You',
    },
    disclosure: 'Acorn Finance (Headway Sales Inc. DBA Acorn Finance, NMLS ID 1817022) is a lending marketplace, not a lender. Acorn Finance is not available in Massachusetts.',
    // Query parameter that carries the financed amount on the calculator results (null = never modify the URL).
    loanAmountParam: 'loanAmount',
  },
  {
    id: 'hfs',
    name: 'HFS Financial',
    url: 'https://www.hfsfinancial.net/promo/6ac5b3e50dbf15abdf42d120',
    buttonLabel: 'Check rates with HFS',
    amountRange: 'Up to $450,000',
    pitch: 'Fixed-rate personal loans with no home equity or appraisal required.',
    bullets: [
      '60-second inquiry, with loan options typically the same day',
      'Fixed rates, terms from 1 to 30 years, no home equity or appraisal',
      'Funds in as little as one business day after approval',
    ],
    facts: {
      loan_amounts: 'Up to $450,000',
      loan_type: 'Fixed-rate personal loan',
      terms: '1–30 years',
      rate_check: 'Soft inquiry, no score impact',
      collateral: 'No home equity or appraisal required',
      prepayment_penalty: 'None',
      origination_fees: 'Set by the lender, shown with your offer',
      funding: 'As little as 1 business day',
      funds_paid_to: 'You',
    },
    disclosure: 'HFS Financial, LLC is a home improvement loan platform, not a lender.',
    loanAmountParam: null,
  },
];

/** Row order + labels of "The facts, in one place" (one column per lender, rendered from `facts`). */
export const FACT_ROWS = [
  ['loan_amounts', 'Loan amounts'],
  ['loan_type', 'Loan type'],
  ['terms', 'Terms'],
  ['rate_check', 'Checking your rate'],
  ['collateral', 'Collateral'],
  ['prepayment_penalty', 'Prepayment penalty'],
  ['origination_fees', 'Origination fees'],
  ['funding', 'Funding'],
  ['funds_paid_to', 'Funds paid to'],
];

/** "Checking your rate with either won't affect your credit score." — wording adapts past two lenders. */
export const financingMicrocopy = (lenders = LENDERS) =>
  `Checking your rate with ${lenders.length === 2 ? 'either' : 'any of them'} won\u2019t affect your credit score.`;

/** "Acorn Finance and HFS Financial" / "A, B and C". */
export const lenderNames = (lenders = LENDERS) => {
  const names = lenders.map((l) => l.name);
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/** The lender link, with the financed amount appended only for lenders that take one (Acorn). */
export const lenderHref = (lender, amount) => {
  const n = Number(amount);
  if (!lender.loanAmountParam || !Number.isFinite(n) || n <= 0) return lender.url;
  const joiner = lender.url.includes('?') ? '&' : '?';
  return `${lender.url}${joiner}${lender.loanAmountParam}=${Math.round(n)}`;
};

export const FINANCING_INTRO =
  'Most of our customers finance some or all of their installation. IDB HVAC Solutions offers financing through two partners. Acorn Finance is a marketplace of 20+ lenders for loans from $1,000 to $100,000. HFS Financial offers fixed-rate personal loans with no home equity or appraisal required. Checking your rate with either takes a few minutes and won\u2019t affect your credit score.';

export const LENDERS_SECTION_TITLE = 'Two ways to check your rate';
export const LENDERS_SECTION_NOTE =
  'Not sure which? Check both. A soft inquiry with each won\u2019t affect your score, and you can take whichever offer is better.';

export const HOW_IT_WORKS = [
  ['Get your number.', 'Use the pre-estimate calculator or book a free on-site measure; your written proposal shows the cash price and an illustrative monthly payment.'],
  ['Check your rate.', 'Pre-qualify with Acorn, HFS, or both; a soft credit inquiry shows your options with no impact to your score.'],
  ['Pick your loan.', 'Compare offers and choose the amount and term that fit; a hard credit inquiry happens only if you move forward with an offer.'],
  ['We install.', 'The lender sends the funds to you, as soon as one business day after approval, and you pay us on the schedule in your proposal.'],
];

export const FACTS_FOOTNOTE =
  'Finance the whole installation, or put down any amount and finance the rest \u00b7 Co-borrowers welcome with Acorn \u00b7 A hard credit inquiry happens only if you proceed with an offer \u00b7 Both are available throughout our Washington service area.';

export const CLOSING_BAND_TEXT =
  'Questions about financing a specific system? Call 425-405-5365 and ask for a proposal with a monthly payment included.';

/** The full disclosure (bottom of /financing and wherever lender buttons appear with room for it). */
export const FINANCING_DISCLOSURE =
  'Financing is offered through Acorn Finance (Headway Sales Inc. DBA Acorn Finance, NMLS ID 1817022), a lending marketplace, and HFS Financial, LLC, a home improvement loan platform, and their networks of lending partners. Neither Acorn Finance nor HFS Financial is a lender. IDB HVAC Solutions is not a lender, does not make credit decisions, and does not charge you for this service. Checking your options uses a soft credit inquiry that does not affect your credit score; a lender may perform a hard credit inquiry if you proceed with an offer, which may affect your score. All loans are subject to credit review and approval by the lender. Rates, terms, fees, and funding times are set by the lender, vary by credit profile, and may change without notice. Monthly payments shown on this site are illustrative estimates, not offers of credit. Acorn Finance is not available in Massachusetts.';

/** The short disclosure used where there is no room for the full text (PDF financing block). */
export const FINANCING_DISCLOSURE_SHORT =
  'Neither Acorn Finance nor HFS Financial is a lender; loans are subject to lender approval. Payment shown is illustrative.';

/** Home page band copy. */
export const HOME_BAND_TEXT =
  'Finance your new system with a fixed-rate loan through Acorn Finance or HFS Financial. Check your rate in minutes with no credit score impact.';
